// components/core/Input.tsx
import React from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { tooLongMessage } from '../constants/textLimits';

/**
 * Base props shared between input and textarea
 */
interface BaseInputProps {
  /** Label text displayed above the input */
  label?: string;
  /** Helper text displayed below the input */
  helperText?: string;
  /** Error message displayed below the input */
  error?: string;
  /** Success message displayed below the input */
  successMessage?: string;
  /** Size variant of the input */
  size?: 'sm' | 'md' | 'lg';
  /** Full width of container */
  fullWidth?: boolean;
  /** Left icon component */
  startIcon?: React.ReactNode;
  /** Right icon component */
  endIcon?: React.ReactNode;
  /** Container className */
  containerClassName?: string;
  rows?: number;
}

/**
 * Props specific to textarea inputs
 */
interface TextAreaInputProps extends BaseInputProps, Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  isTextArea: true;
  rows?: number;
}

/**
 * Props specific to regular inputs
 */
interface StandardInputProps extends BaseInputProps, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  isTextArea?: false;
}

/**
 * Combined input props type
 */
type InputProps = TextAreaInputProps | StandardInputProps;

/**
 * Size-specific styles mapping
 */
const sizeStyles = {
  sm: 'h-8 text-sm px-2',
  md: 'h-10 text-base px-3',
  lg: 'h-12 text-lg px-4'
};

/**
 * Input component that supports both regular inputs and textareas.
 *
 * When a `label` is provided, it is associated with the rendered control via
 * `htmlFor`/`id` (WCAG 1.3.1) so screen readers announce it and
 * `getByLabelText()` queries resolve it. An explicitly-passed `id` prop is
 * always honoured; otherwise a stable id is generated with `React.useId()`.
 */
export const Input = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      successMessage,
      size = 'md',
      fullWidth = false,
      startIcon,
      endIcon,
      containerClassName,
      className,
      isTextArea,
      rows = 3,
      id,
      ...props
    },
    ref
  ) => {
    // An explicitly-passed id always wins over the generated one. The
    // generated id is only needed to associate a label with its control, so
    // it's only applied when a label is actually rendered — otherwise the
    // control gets whatever id (if any) the caller explicitly passed, same
    // as before this fix.
    const generatedId = React.useId();
    const inputId = label ? (id ?? generatedId) : id;

    // A `maxLength` stops typing without a word, so the field says how close
    // the text is once it is within a tenth of the limit, and calls text that
    // is already over it (a stored record, a suggestion) an error (T119).
    const limit = typeof props.maxLength === 'number' ? props.maxLength : undefined;
    const length = typeof props.value === 'string' ? props.value.length : undefined;
    const count = (n: number) => n.toLocaleString('en-US');
    const overLimit = limit !== undefined && length !== undefined && length > limit;
    const lengthError = overLimit ? tooLongMessage(length, limit) : undefined;
    const lengthNote = limit !== undefined && length !== undefined && !overLimit && length >= limit * 0.9
      ? `${count(length)} of ${count(limit)} characters`
      : undefined;
    const shownError = error || lengthError;

    // The message under the field describes the field itself, so a screen
    // reader on it hears why it is invalid, not only that it is (A11Y-005).
    // A description the caller passed is kept, ahead of this one.
    const message = shownError || successMessage || lengthNote || helperText;
    const messageId = `${inputId ?? generatedId}-message`;
    const describedBy =
      [props['aria-describedby'], message ? messageId : undefined].filter(Boolean).join(' ') ||
      undefined;

    const inputStyles = twMerge(
      clsx(
        'w-full rounded-lg border transition-colors duration-200',
        'focus:outline-none',
        !isTextArea && sizeStyles[size],
        isTextArea && 'p-3',
        startIcon && 'pl-10',
        endIcon && 'pr-10',
        
        // Theme-specific classes
        `input`,
        shownError && `input-error`,
        successMessage && `input-success`,
        className
      )
    );

    const containerStyles = twMerge(
      clsx(
        'flex flex-col',
        fullWidth && 'w-full',
        containerClassName
      )
    );

    return (
      <div className={containerStyles}>
        {label && (
          <label
            htmlFor={inputId}
            className={clsx(
              'mb-1.5 text-sm font-medium',
              `form-label`
            )}
          >
            {label}
          </label>
        )}
        <div className="relative">
          {startIcon && (
            <div className={clsx(
              "absolute left-3 top-1/2 -translate-y-1/2",
              `typography-secondary`
            )}>
              {startIcon}
            </div>
          )}
          {isTextArea ? (
            <textarea
              ref={ref as React.Ref<HTMLTextAreaElement>}
              id={inputId}
              className={inputStyles}
              rows={rows}
              {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
              aria-describedby={describedBy}
              aria-invalid={overLimit || props['aria-invalid']}
            />
          ) : (
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              id={inputId}
              className={inputStyles}
              {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
              aria-describedby={describedBy}
              aria-invalid={overLimit || props['aria-invalid']}
            />
          )}
          {endIcon && (
            <div className={clsx(
              "absolute right-3 top-1/2 -translate-y-1/2",
              `typography-secondary`
            )}>
              {endIcon}
            </div>
          )}
        </div>
        {message && (
          <p id={messageId} className={clsx(
            'mt-1.5 text-sm',
            shownError && `form-error`,
            successMessage && `form-success`,
            !shownError && !successMessage && `form-helper`
          )}>
            {message}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;